import { AreaCalculationTypes, FloorPlanCertificationPropertyTypes } from 'constants/purchaseFlow/floorPlanCertification'
import { EntityKeys, QueryType, retryUnlessNotFound } from 'utils/reactQuery'

import { useQuery } from '@tanstack/react-query'
import { AIEditingDTO } from 'models/purchaseFlow/AIEditingDTO'
import { AuthoritiesDocumentDTO } from 'models/purchaseFlow/AuthoritiesDocumentDTO'
import { ConsumptionCertificateDetailsDTO } from 'models/purchaseFlow/consumptionCertificateDetailsDTO'
import { EnergyCertificateDetailsDTO } from 'models/purchaseFlow/energyCertificateOfNeedDTO'
import { FloorPlanCertificationFloorDTO } from 'models/purchaseFlow/floorPlanCertificationDTO'
import { FloorPlanDetailsDTO } from 'models/purchaseFlow/floorPlanDTO'
import { MeasurementOnSiteRequestDTO } from 'models/purchaseFlow/measurementDTO'
import { AssignmentStagingConfiguration } from 'models/virtualStaging/staging'
import { useAPI } from 'utils/API'

// ---- Uploaded Files ----

export interface UploadedFileDTO {
  filename: string
  originalFilename: string
  signedUrl: string
}

enum UploadedFilesEndpoints {
  GET = '/workspace/{workspaceId}/assignment/{assignmentId}/config-details/uploaded-files',
}

export function useGetAssignmentUploadedFiles(assignmentId: string, workspaceId: string) {
  const api = useAPI<UploadedFilesEndpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET, 'UPLOADED_FILES', { assignmentId, workspaceId }],
    queryFn: () => api.get<UploadedFileDTO[]>(
      UploadedFilesEndpoints.GET,
      { assignmentId, workspaceId },
      false
    ),
    enabled: !!assignmentId && !!workspaceId,
    retry: retryUnlessNotFound,
  })
}

// ---- Floor Plan Details ----

enum FloorPlanDetailsEndpoints {
  GET = '/workspace/{workspaceId}/assignment/{assignmentId}/config-details/floor-plan',
}

export function useGetAssignmentFloorPlanDetails(assignmentId: string, workspaceId: string) {
  const api = useAPI<FloorPlanDetailsEndpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET, 'FLOOR_PLAN', { assignmentId, workspaceId }],
    queryFn: () => api.get<FloorPlanDetailsDTO>(
      FloorPlanDetailsEndpoints.GET,
      { assignmentId, workspaceId },
      false
    ),
    enabled: !!assignmentId && !!workspaceId,
    retry: retryUnlessNotFound,
  })
}

// ---- Virtual Staging Details ----

enum VirtualStagingDetailsEndpoints {
  GET = '/workspace/{workspaceId}/assignment/{assignmentId}/config-details/virtual-staging',
}

export function useGetAssignmentStagingDetails(
  assignmentId: string,
  workspaceId: string,
  options?: { enabled?: boolean }
) {
  const api = useAPI<VirtualStagingDetailsEndpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET, 'STAGING_DETAILS', { assignmentId, workspaceId }],
    queryFn: () => api.get<AssignmentStagingConfiguration>(
      VirtualStagingDetailsEndpoints.GET,
      { assignmentId, workspaceId },
      false
    ),
    enabled: !!assignmentId && !!workspaceId && (options?.enabled ?? true),
    retry: retryUnlessNotFound,
  })
}

// ---- Authorities Documents Details ----

enum AuthoritiesDocumentsDetailsEndpoints {
  GET = '/workspace/{workspaceId}/assignment/{assignmentId}/config-details/authorities-document',
}

export function useGetAssignmentAuthoritiesDocumentsDetails(assignmentId: string, workspaceId: string) {
  const api = useAPI<AuthoritiesDocumentsDetailsEndpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET, 'AUTHORITIES_DOCUMENTS_DETAILS', { assignmentId, workspaceId }],
    queryFn: () => api.get<AuthoritiesDocumentDTO>(
      AuthoritiesDocumentsDetailsEndpoints.GET,
      { assignmentId, workspaceId },
      false
    ),
    enabled: !!assignmentId && !!workspaceId,
    retry: retryUnlessNotFound,
  })
}

// ---- Measurement On-Site Details ----

enum MeasurementOnSiteEndpoints {
  GET = '/workspace/{workspaceId}/assignment/{assignmentId}/config-details/measurement-on-site',
}

export function useGetAssignmentMeasurementOnSiteDetails(assignmentId: string, workspaceId: string) {
  const api = useAPI<MeasurementOnSiteEndpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET, 'MEASUREMENT_ON_SITE_DETAILS', { assignmentId, workspaceId }],
    queryFn: () => api.get<MeasurementOnSiteRequestDTO>(
      MeasurementOnSiteEndpoints.GET,
      { assignmentId, workspaceId },
      false
    ),
    enabled: !!assignmentId && !!workspaceId,
    retry: retryUnlessNotFound,
  })
}

// ---- AI Editing Details ----

enum AIEditingEndpoints {
  GET = '/workspace/{workspaceId}/assignment/{assignmentId}/config-details/ai-editing',
}

export function useGetAssignmentAIEditingDetails(assignmentId: string, workspaceId: string) {
  const api = useAPI<AIEditingEndpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET, 'AI_EDITING', { assignmentId, workspaceId }],
    queryFn: () => api.get<AIEditingDTO>(
      AIEditingEndpoints.GET,
      { assignmentId, workspaceId },
      false
    ),
    enabled: !!assignmentId && !!workspaceId,
    retry: retryUnlessNotFound,
  })
}

// ---- Certificate of Needs Details ----

enum CertificateOfNeedsEndpoints {
  GET = '/workspace/{workspaceId}/assignment/{assignmentId}/config-details/certificate-of-needs',
}

export function useGetCertificateOfNeedsDetails(assignmentId: string, workspaceId: string) {
  const api = useAPI<CertificateOfNeedsEndpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET, 'CERTIFICATE_OF_NEEDS', { assignmentId, workspaceId }],
    queryFn: () => api.get<EnergyCertificateDetailsDTO>(
      CertificateOfNeedsEndpoints.GET,
      { assignmentId, workspaceId },
      false
    ),
    enabled: !!assignmentId && !!workspaceId,
    retry: retryUnlessNotFound,
  })
}

// ---- Consumption Certificate Details ----

enum ConsumptionCertificateEndpoints {
  GET = '/workspace/{workspaceId}/assignment/{assignmentId}/config-details/consumption-certificate',
}

export function useGetConsumptionCertificateDetails(assignmentId: string, workspaceId: string) {
  const api = useAPI<ConsumptionCertificateEndpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET, 'CONSUMPTION_CERTIFICATE', { assignmentId, workspaceId }],
    queryFn: () => api.get<ConsumptionCertificateDetailsDTO>(
      ConsumptionCertificateEndpoints.GET,
      { assignmentId, workspaceId },
      false
    ),
    enabled: !!assignmentId && !!workspaceId,
    retry: retryUnlessNotFound,
  })
}

// ---- Floor Plan Certification (Living Area Calculation / Virtual Tour) Details ----

/** Response DTO for floor plan certification assignment details. */
export interface FloorPlanCertificationDetailsDTO {
  propertyType: FloorPlanCertificationPropertyTypes
  floors: FloorPlanCertificationFloorDTO[]
  regulatedLivingAreaCalculation: AreaCalculationTypes | null
  requireStampOfArchitect?: boolean
  comment?: string
}

enum FloorPlanCertificationDetailsEndpoints {
  GET = '/workspace/{workspaceId}/assignment/{assignmentId}/config-details/floor-plan-certification',
}

export function useGetAssignmentFloorPlanCertificationDetails(assignmentId: string, workspaceId: string) {
  const api = useAPI<FloorPlanCertificationDetailsEndpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET, 'FLOOR_PLAN_CERTIFICATION', { assignmentId, workspaceId }],
    queryFn: () => api.get<FloorPlanCertificationDetailsDTO>(
      FloorPlanCertificationDetailsEndpoints.GET,
      { assignmentId, workspaceId },
      false
    ),
    enabled: !!assignmentId && !!workspaceId,
    retry: retryUnlessNotFound,
  })
}

// ---- Virtual Visit Details ----

export interface VirtualVisitDetailsDTO {
  virtualVisitUrls: string[]
}

enum VirtualVisitDetailsEndpoints {
  GET = '/workspace/{workspaceId}/assignment/{assignmentId}/config-details/virtual-visit',
}

export function useGetAssignmentVirtualVisitDetails(assignmentId: string, workspaceId: string) {
  const api = useAPI<VirtualVisitDetailsEndpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET, 'VIRTUAL_VISIT', { assignmentId, workspaceId }],
    queryFn: () => api.get<VirtualVisitDetailsDTO>(
      VirtualVisitDetailsEndpoints.GET,
      { assignmentId, workspaceId },
      false
    ),
    enabled: !!assignmentId && !!workspaceId,
    retry: retryUnlessNotFound,
  })
}
